import React, { useEffect, useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Columns3, RotateCcw, Save, Bookmark, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { useConfirm } from './useConfirm';

/**
 * Column picker with **named saved presets**.
 *
 * Why: a fleet manager doing yard walk-arounds wants "Compact" (Unit #, Status,
 * Meter, Depot). The office user reviewing compliance wants "Compliance" (Unit
 * #, Serial, Status, Depot). Without presets they'd have to retick boxes every
 * time they swap workflows. Presets store both the column selection AND let
 * users star one as the default.
 *
 * Storage shape (localStorage key passed via `storageKey`):
 *   {
 *     visible: ["unit_number","status","hours"],   // current selection
 *     presets: [{ id, name, columns: [...] }],     // named presets
 *     activeId: "preset-uuid" | null,              // currently selected preset
 *   }
 */
const PRESET_PREFIX = 'preset-';

const newPresetId = () => `${PRESET_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const ColumnPicker = ({
  columns,
  visible,
  onVisibleChange,
  onReset,
  storageKey = 'column-picker',  // unique per table — namespaces presets
  testId = 'column-picker',
}) => {
  const { confirm, dialogProps } = useConfirm();
  const [presets, setPresets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const visibleCount = useMemo(() => visible.size, [visible]);

  // Load presets from localStorage on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${storageKey}-presets`);
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (Array.isArray(obj?.presets)) setPresets(obj.presets);
      if (obj?.activeId) setActiveId(obj.activeId);
    } catch {
      /* corrupt cache — silently reset */
    }
  }, [storageKey]);

  // Persist whenever presets/active change.
  useEffect(() => {
    try {
      localStorage.setItem(
        `${storageKey}-presets`,
        JSON.stringify({ presets, activeId }),
      );
    } catch { /* private-mode quota */ }
  }, [presets, activeId, storageKey]);

  const toggle = (key, required) => {
    if (required) return;
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onVisibleChange(next);
    setActiveId(null); // mutating columns drops us out of an applied preset
  };

  const applyPreset = (preset) => {
    const set = new Set(preset.columns);
    columns.forEach(c => { if (c.required) set.add(c.key); }); // force required
    onVisibleChange(set);
    setActiveId(preset.id);
    toast.success(`View "${preset.name}" applied`);
  };

  const saveAsPreset = () => {
    const name = saveName.trim();
    if (!name) { toast.error('Name is required'); return; }
    if (presets.some(p => p.name.toLowerCase() === name.toLowerCase())) {
      toast.error('A view with that name already exists');
      return;
    }
    const p = { id: newPresetId(), name, columns: [...visible] };
    setPresets(prev => [...prev, p]);
    setActiveId(p.id);
    setSaveOpen(false);
    setSaveName('');
    toast.success(`Saved "${name}"`);
  };

  const overwritePreset = () => {
    if (!activeId) return;
    setPresets(prev => prev.map(p => p.id === activeId ? { ...p, columns: [...visible] } : p));
    toast.success('View updated');
  };

  const deletePreset = (id) => {
    const p = presets.find(x => x.id === id);
    if (!p) return;
    confirm({
      title: `Delete saved view "${p.name}"?`,
      description: 'This saved view will be permanently removed.',
      testId: 'confirm-delete-saved-view',
      onConfirm: async () => {
        setPresets(prev => prev.filter(x => x.id !== id));
        if (activeId === id) setActiveId(null);
        toast.success(`Deleted "${p.name}"`);
      },
    });
  };

  const activePreset = presets.find(p => p.id === activeId);

  return (
    <>
      <div className="flex items-center gap-1">
        {/* Active-preset chip (only when one is applied) */}
        {activePreset && (
          <span
            className="text-[10px] uppercase tracking-widest text-primary border border-primary/40 bg-primary/10 px-2 py-1 rounded-sm font-mono"
            data-testid={`${testId}-active-chip`}
          >
            <Bookmark className="w-3 h-3 inline mr-1" />
            {activePreset.name}
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`${testId}-trigger`}>
              <Columns3 className="w-3.5 h-3.5 mr-2" />
              Columns
              <span className="ml-1.5 text-[10px] text-muted-foreground font-mono">
                {visibleCount}/{columns.length}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-testid={`${testId}-menu`}>
            <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
              Show columns
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={visible.has(col.key)}
                disabled={col.required}
                onCheckedChange={() => toggle(col.key, col.required)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`${testId}-toggle-${col.key}`}
              >
                <span className="flex-1">{col.label}</span>
                {col.required && (
                  <span className="text-[9px] text-muted-foreground ml-2">required</span>
                )}
              </DropdownMenuCheckboxItem>
            ))}

            {/* Saved views */}
            {presets.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
                  Saved views
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={activeId || ''}>
                  {presets.map(p => (
                    <div key={p.id} className="flex items-center pr-2 hover:bg-accent rounded-sm">
                      <DropdownMenuRadioItem
                        value={p.id}
                        className="flex-1"
                        onSelect={(e) => { e.preventDefault(); applyPreset(p); }}
                        data-testid={`${testId}-preset-${p.id}`}
                      >
                        <span className="flex-1">{p.name}</span>
                      </DropdownMenuRadioItem>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive ml-1 p-1"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); deletePreset(p.id); }}
                        title="Delete this saved view"
                        data-testid={`${testId}-preset-delete-${p.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </DropdownMenuRadioGroup>
              </>
            )}

            <DropdownMenuSeparator />
            {activeId && (
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); overwritePreset(); }}
                data-testid={`${testId}-overwrite`}
              >
                <Check className="w-3.5 h-3.5 mr-2" />
                Update "{activePreset?.name}"
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); setSaveOpen(true); }}
              data-testid={`${testId}-save-as`}
            >
              <Save className="w-3.5 h-3.5 mr-2" />
              Save current as view…
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => { onReset(); setActiveId(null); }}
              data-testid={`${testId}-reset`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-2" />
              Reset to defaults
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Save-as dialog */}
      <AlertDialog open={saveOpen} onOpenChange={setSaveOpen}>
        <AlertDialogContent data-testid={`${testId}-save-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-primary" />
              Save current view
            </AlertDialogTitle>
            <AlertDialogDescription>
              Save your current column selection as a named view you can switch to with one click.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">View name</Label>
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Yard walk-around"
              onKeyDown={(e) => { if (e.key === 'Enter') saveAsPreset(); }}
              data-testid={`${testId}-save-name-input`}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-save-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={saveAsPreset} data-testid={`${testId}-save-confirm`}>
              Save view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConfirmDeleteDialog {...dialogProps} />
    </>
  );
};

export default ColumnPicker;
